import React, { useEffect, useState } from 'react'
import classes from './styles.module.scss'
// import { FormattedMessage } from 'react-intl'
import messages from './messages'
import { Col, Row, Button, Icon } from 'antd'
import { WhiteSpace, Loading, CopyToClipboard } from 'components'
import { handleAmount, intl, recordCountlyEvent, yearlyRateToMonthlyPercent, postMessage } from 'utils'
import {
  formatExchangeRateForDisplay,
  getDccAmountDecimalPlaces,
  normalizeMarkupPercentDisplay,
  payResultHasDccDetails,
} from 'utils/dccHelpers'
import WingBlank from 'components/WingBlank'
import { Flex } from 'antd-mobile'
import LoginDrawer from 'components/LoginDrawer'
import PaymentMethodIcon from 'components/PaymentMethodIcon'
import { PoweredBy } from 'components/TermsAndPrivacy/TermsAndPrivacy'
import TipsResult from 'components/TipsResult'
import GooglePayMark from 'components/PaymentMethodIcon/images/Google Pay.svg'
import Countdown from 'react-countdown-now'
import _ from 'lodash'
import queryString from 'query-string'

// Totok app doesn't support download receipt
const showDownloadReceipt = () => {
  return !navigator.userAgent.toLowerCase().includes('totok')
}

const formatInstallmentFrequency = (frequency) => {
  if (!frequency) return ''
  const freqMap = {
    MONTHS: 'month',
    MONTH: 'month',
    month: 'month',
  }
  const key = `${frequency}`.toUpperCase()
  return freqMap[key] || `${frequency}`.toLowerCase()
}

const formatPercent = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0%'
  return `${parseFloat(n.toFixed(2))}%`
}

const buildFallbackPaymentMethod = (payResultInfo) => {
  // If backend doesn't return payResultInfo.paymentMethod (seen in some installment flows),
  // synthesize the same shape so we reuse the existing PaymentMethod row renderer.
  const supplierCode = _.get(payResultInfo, 'installmentInfo.supplierCode')
  const cardBrand =
    supplierCode && typeof supplierCode === 'string' ? supplierCode.toLowerCase() : undefined

  // Prefer cached card details captured during payment initiation (keeps same UI as normal card payments)
  const orderTokenForCache = _.get(payResultInfo, 'orderToken') || queryString.parse(window.location.search)?.orderToken
  let cached = null
  try {
    cached = orderTokenForCache
      ? JSON.parse(sessionStorage.getItem(`paypage-paymentMethod-${orderTokenForCache}`) || 'null')
      : null
  } catch (e) {
    cached = null
  }

  const cachedCardBrand = _.get(cached, 'cardBrand')
  const cachedCardNoSuffix = _.get(cached, 'cardNoSuffix')
  const cachedDebitCredit = _.get(cached, 'debitCredit')
  const cachedDescription = _.get(cached, 'description')

  // Try common "last4" fields that may exist in different integrations.
  const cardNoSuffix =
    cachedCardNoSuffix ||
    _.get(payResultInfo, 'cardNoSuffix') ||
    _.get(payResultInfo, 'payMethod.cardNoSuffix') ||
    _.get(payResultInfo, 'payMethod.payMethodExtension.cardNoSuffix') ||
    _.get(payResultInfo, 'paymentMethod.extension.cardNoSuffix') ||
    _.get(payResultInfo, 'paymentMethod.extension.cardNoLast4') ||
    _.get(payResultInfo, 'payMethodExtension.cardNoSuffix')

  const debitCredit =
    cachedDebitCredit ||
    _.get(payResultInfo, 'debitCredit') ||
    _.get(payResultInfo, 'payMethod.payMethodExtension.debitCredit') ||
    _.get(payResultInfo, 'payMethodExtension.debitCredit')

  const cardTypeLabel = debitCredit === 'DC' ? 'Debit Card' : debitCredit === 'CC' ? 'Credit Card' : 'Card'

  const finalCardBrand = cachedCardBrand || cardBrand
  if (finalCardBrand) {
    const brandLabel = supplierCode || String(finalCardBrand).toUpperCase()
    const description =
      (cachedDescription && `${cachedDescription}`.trim()) ||
      (cardNoSuffix
        ? `${brandLabel} ${cardTypeLabel}(${cardNoSuffix})`
        : _.get(payResultInfo, 'installmentInfo')
          ? `${brandLabel} Installment`
          : `${brandLabel} ${cardTypeLabel}`)

    return {
      type: 'BANK_CARD',
      description,
      extension: { cardBrand: finalCardBrand },
    }
  }
  return null
}

// Google Pay 的设备支付渠道号，与 PaymentMethodIcon / DevicePay 中的 36 一致
const GOOGLE_PAY_CHANNEL = '36'

const isGooglePayMethod = (paymentMethod) =>
  String(_.get(paymentMethod, 'extension.payChannel') || '') === GOOGLE_PAY_CHANNEL ||
  /google\s*pay/i.test(_.get(paymentMethod, 'description') || '')

const getPaymentMethodDescription = (paymentMethod) => {
  // Google Pay 品牌规范：标志旁的文字只能是 "Google Pay"，不能带卡号或合作方前缀
  if (isGooglePayMethod(paymentMethod)) return 'Google Pay'
  const description = _.get(paymentMethod, 'description')
  const cardBrand = String(_.get(paymentMethod, 'extension.cardBrand') || '').toLowerCase()
  if (cardBrand === 'jaywan' && description && !/jaywan/i.test(description)) {
    return `Jaywan ${description}`
  }
  return description
}

const ENUM = {
  P: {
    icon: 'iconfont iconcomm-time-circle yellow status-icon',
    text: intl().formatMessage(messages.Processing),
    // Prefer PNG for broader compatibility (some environments don't render external SVG in <img> reliably)
    img: require('./images/state-waiting.png'),
  },
  S: {
    icon: 'iconfont iconcomm-check-circle-fill green status-icon',
    text: intl().formatMessage(messages.PaidSuccessfully),
    img: require('./images/state-Success.png'),
  },
  F: {
    icon: 'iconfont iconcomm-close_fill1 red status-icon',
    text: intl().formatMessage(messages.Failed),
    img: require('./images/state-Failed.png'),
  },
}

/**
 * 支付成功后倒计时多少秒再自动跳回商户。给用户看清结果的时间，同时不至于卡住。
 * PRD 若规定了具体秒数，改这一个常量即可。
 */
const AUTO_REDIRECT_DELAY_SECONDS = 3

/**
 * 自动跳转（无用户手势）只接受 http/https 绝对地址或站内相对路径：拒绝 javascript:/data:
 * 等 scheme 注入，也拒绝 //host 协议相对地址（会被解析到外域）。
 * 用户手动点击的跳转不走这里——深链回跳（如 merchantapp://）依赖历史行为。
 */
const sanitizeAutoRedirectUrl = (url) => {
  const value = typeof url === 'string' ? url.trim() : ''
  // "/" 后再跟 "/" 或 "\" 都会被浏览器当成协议相对地址解析到外域，一并拒绝
  return /^(https?:\/\/|\/(?![/\\]))/i.test(value) ? value : ''
}

// 用户点击触发的回商户跳转，保持既有无条件行为（商户深链场景依赖它）
const redirectToMerchantUrl = (url, isIframe) => {
  if (isIframe && url) {
    postMessage({ name: 'PAYMENT_SUCCESS', redirectURI: url })
    window.top.location.href = url
  } else if (url) {
    window.location.href = url
  }
}

/**
 * 商户没传 redirectUrl 时 Done 的收尾动作：没有可回跳的地址，唯一能做的是关掉页面。
 * App webview 里能关掉；普通浏览器标签会静默拒绝（只允许关脚本自己开的窗口），
 * 这时回调 onRefused，由调用方换成"请手动关闭"提示。
 *
 * 不要在这里退回上一页：历史栈上一页是收银台支付页，订单已支付，回去只会看到
 * "The operation has been expired" 之类的报错，比没反应更糟。
 */
const closePageOrNotify = (onRefused) => {
  window.close()
  // close() 被拒时不抛错也不同步改变 closed，给浏览器一帧时间再判断
  setTimeout(() => {
    if (!window.closed) onRefused()
  }, 150)
}

const isTipsReturnUrl = (merchantReturnUrl) =>
  !!merchantReturnUrl && /\/pay-page\/tips-result/.test(merchantReturnUrl)

/**
 * 是否走"支付成功后倒计时自动跳回商户"：只覆盖成功态；tips 订单有自己的结果页；
 * iframe 由父页面负责跳转；URL 缺失或未通过自动跳转校验则无处可跳，都排除。
 *
 * 存卡流程靠 cancelled 感知用户交互来让路（用户点了存卡入口就取消倒计时），
 * 不能用"页面上有存卡入口"（showLoginFields）一刀切禁用：银行卡+未登录是最主流的
 * 支付场景，那样等于把自动跳转整个架空。
 */
const shouldAutoRedirectToMerchant = ({ autoRedirect, status, merchantReturnUrl, isIframe }, cancelled) =>
  !!autoRedirect &&
  status === 'S' &&
  !cancelled &&
  !!sanitizeAutoRedirectUrl(merchantReturnUrl) &&
  !isTipsReturnUrl(merchantReturnUrl) &&
  !isIframe

/**
 * 可见的倒计时 + 到点跳转。渲染在结果页顶部金额下方（用户视线焦点处），
 * 倒计时归零后上报埋点并跳回商户。复用项目里已有的 react-countdown-now（OTP/SMSCode 同款）。
 * 只在 shouldAutoRedirectToMerchant 为真时挂载，此时必然是非 iframe 的成功态。
 */
const AutoRedirectCountdown = ({ merchantReturnUrl, pageName, payResultInfo = {} }) => {
  // 目标时间只初始化一次，父组件重渲染不会重置倒计时
  const [date] = useState(() => Date.now() + AUTO_REDIRECT_DELAY_SECONDS * 1000)
  return (
    <Countdown
      date={date}
      onComplete={() => {
        // 埋点/跳转等副作用只能放 onComplete：renderer 在渲染期执行，重渲染会重复触发
        const safeUrl = sanitizeAutoRedirectUrl(merchantReturnUrl)
        if (!safeUrl) return
        pageName &&
          recordCountlyEvent({
            page_name: pageName,
            event_name: 'click',
            biz_tags: {
              trade_voucher_no: payResultInfo.orderNo,
              element_name: 'auto_redirect',
              status: 'S',
            },
          })
        // replace 而非 href：把结果页从会话历史里换掉，避免返回键"回来又被跳走"的循环
        window.location.replace(safeUrl)
      }}
      renderer={({ total, completed }) => (
        <div className='text-center paragraph_regular text_secondary'>
          {intl().formatMessage(messages.RedirectingInSeconds, {
            seconds: completed ? 0 : Math.ceil(total / 1000),
          })}
        </div>
      )}
    />
  )
}

const HandleButtonsWrapper = (props) => {
  return (
    <Flex
      align='center'
      direction='column'
      justify='center'
      style={{ marginTop: 'auto', paddingRight: '16px', paddingLeft: '16px', borderTop: '1px solid var(--gray-4)' }}
    >
      <WhiteSpace />
      <div className='w-full'>{props.children}</div>
      <WhiteSpace />
    </Flex>
  )
}

const HandleButtons = (props) => {
  const {
    status,
    merchantReturnUrl,
    canTryAgain,
    payResultInfo = {},
    pageName,
    siteConfig,
    handleTipsSaveResultVisible,
    isIframe,
    shouldAutoRedirect,
  } = props
  const btnProps = {}
  if (siteConfig?.isMobile) {
    btnProps.block = true
    btnProps.size = 'large'
  }
  const redirectToMerchant = (url) => redirectToMerchantUrl(url, isIframe)
  const recordEvent = (data) => {
    pageName &&
      recordCountlyEvent({
        page_name: pageName,
        event_name: 'click',
        biz_tags: {
          trade_voucher_no: payResultInfo.orderNo,
          ...data,
        },
      })
  }
  // 浏览器拒绝关闭页面后，把 Done 换成手动关闭提示，避免留下一个点了没反应的按钮
  const [closeRefused, setCloseRefused] = useState(false)
  const isTipsOrder = isTipsReturnUrl(merchantReturnUrl)
  useEffect(() => {
    if (isTipsOrder && status === 'S' && typeof handleTipsSaveResultVisible === 'function') {
      handleTipsSaveResultVisible(true)
    }
  }, [isTipsOrder, status])

  if (status === 'P') {
    return (
      <HandleButtonsWrapper>
        <div>
          <Button
            type='default'
            {...btnProps}
            onClick={() => {
              recordEvent({ element_name: 'refresh' })
              window.location.reload()
            }}
          >
            {intl().formatMessage(messages.Refresh)}
          </Button>

          {merchantReturnUrl && !isTipsOrder && !isIframe && (
            <>
              <WhiteSpace />
              <Button
                type='primary'
                {...btnProps}
                onClick={() => {
                  recordEvent({ element_name: 'finish', status })
                  redirectToMerchant(merchantReturnUrl)
                }}
              >
                {intl().formatMessage(messages.Finish)}
              </Button>
            </>
          )}
        </div>
      </HandleButtonsWrapper>
    )
  }
  if (status === 'S' && !isTipsOrder) {
    // In iframe result, hide Done/Finish button; parent handles redirect
    if (isIframe) return null
    // 自动跳转接管（倒计时显示在页面顶部金额下方），不再需要 Done。
    if (shouldAutoRedirect) return null
    // 无 redirectUrl 时页面关不掉：换成手动关闭提示，不留死按钮
    if (closeRefused) {
      return (
        <HandleButtonsWrapper>
          <div className='text-center paragraph_regular text_secondary'>
            {intl().formatMessage(messages.YouCanClosePage)}
          </div>
        </HandleButtonsWrapper>
      )
    }
    // 成功页始终给用户一个收尾入口：有 redirectUrl 回商户，没有就关闭页面。
    return (
      <HandleButtonsWrapper>
        <Button
          type='primary'
          {...btnProps}
          onClick={() => {
            if (merchantReturnUrl) {
              recordEvent({ element_name: 'finish', status })
              redirectToMerchant(merchantReturnUrl)
            } else {
              recordEvent({ element_name: 'close_page', status })
              closePageOrNotify(() => setCloseRefused(true))
            }
          }}
        >
          {intl().formatMessage(messages.Finish)}
        </Button>
      </HandleButtonsWrapper>
    )
  }
  if (status === 'F') {
    return (
      <HandleButtonsWrapper>
        <div>
          {merchantReturnUrl && !isTipsOrder && !isIframe && (
            <Button
              type={canTryAgain ? 'default' : 'primary'}
              {...btnProps}
              onClick={() => {
                recordEvent({ element_name: 'finish', status })
                redirectToMerchant(merchantReturnUrl)
              }}
            >
              {intl().formatMessage(messages.Finish)}
            </Button>
          )}
          {canTryAgain && (
            <Button
              type='primary'
              {...btnProps}
              onClick={() => {
                recordEvent({ element_name: 'try_again' })
                window.location.href = isIframe
                  ? `/iframe/pay-page?ft=${props.orderToken}`
                  : `/pay-page/main?ft=${props.orderToken}`
              }}
            >
              {intl().formatMessage(messages.TryAgain)}
            </Button>
          )}
        </div>
      </HandleButtonsWrapper>
    )
  }
  return null
}

function ToSaveCard(props) {
  if (props.showLoginFields) {
    return (
      <React.Fragment>
        <WhiteSpace />
        <Flex className={classes.loginFields} onClick={() => props.showLoginDrawer(props.payResultInfo, 'card')}>
          <img width={24} src={require('./images/save_card.svg')} />
          <Flex.Item className='text-left'>
            <div className='paragraph_regular'>{intl().formatMessage(messages.SavedCardfornexttime)}</div>
            <div className='caption_regular text_secondary'>{intl().formatMessage(messages.Onlyoneclick)}</div>
          </Flex.Item>
          <Icon type='right' />
        </Flex>
        <LoginDrawer
          title={intl().formatMessage(messages.SavedCardfornexttime)}
          btnText={intl().formatMessage(messages.ToSave)}
          visible={props.LoginDrawerVisible}
          forceAsDrawer
          pcProps={{
            getContainer: '#container',
            width: '50%',
          }}
          onChange={props.onChange}
          type='LOGIN_TO_SAVE_CARD'
        />
      </React.Fragment>
    )
  }
  return null
}

export default class Result extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      tipsSaveResultVisible: false,
      // 用户主动开始存卡后就不再自动跳转（一旦取消不恢复，避免关掉抽屉又被送走）
      autoRedirectCancelled: false,
    }
  }

  handleTipsSaveResultVisible = (visible) => {
    this.setState({ tipsSaveResultVisible: visible })
  }

  /**
   * 用户在成功页上主动做了"需要停留"的操作时调用：取消自动跳转，且不再恢复。
   * 新增这类交互（会离开页面或需要等待的）时，记得一并调用这里。
   */
  cancelAutoRedirect = () => {
    if (!this.state.autoRedirectCancelled) {
      this.setState({ autoRedirectCancelled: true })
    }
  }

  showLoginDrawer = (payResultInfo, type) => {
    const { pageName } = this.props
    pageName &&
      recordCountlyEvent({
        page_name: pageName,
        element_name: 'save_card',
        event_name: 'click',
        biz_tags: {
          trade_voucher_no: payResultInfo.orderNo,
          type,
        },
      })
    this.cancelAutoRedirect()
    this.setState({ LoginDrawerVisible: true })
  }

  render() {
    const {
      loading,
      status,
      failMessage,
      forceMobile,
      payResultInfo = {},
      showLoginFields,
      banner,
      siteConfig,
    } = this.props

    // 只在这里判定一次，倒计时和底部按钮共用，保证两者互斥不脱节
    const shouldAutoRedirect = shouldAutoRedirectToMerchant(this.props, this.state.autoRedirectCancelled)
    const resolvedPaymentMethod =
      payResultInfo.paymentMethod || buildFallbackPaymentMethod(payResultInfo)
    const dccAcceptedCurrency = _.get(payResultInfo, 'dccTotalAmount.currency')
    const hideCopyright = _.get(siteConfig, 'pageCustomize.hidePowerByPayBy') === true
    const shouldHideCopyrightForPaymentMethod = true

    var rootClassName = classes.componentsResult
    if (forceMobile) {
      // 强制指定按移动端样式显示
      rootClassName += ' ' + classes.forceMobile
    }

    const handleDownloadPDF = () => {
      if (typeof this.props.downloadPDF !== 'function') return
      // 下载要等接口返回后才跳 fileLink，倒计时必须让路，否则回执会被跳转吞掉
      this.cancelAutoRedirect()
      const { orderToken } = this.props
      const queryParams = queryString.parse(window.location.search)
      const paymentOrderNo =
        _.get(payResultInfo, 'paymentOrderNo') ||
        this.props.paymentOrderNo ||
        queryParams.paymentOrderNo

      // paymentOrderNo is mandatory for /cashierii/pay/v1/unauth/pay-result-file
      if (!paymentOrderNo) {
        console.error('Download receipt failed: missing paymentOrderNo', {
          orderToken,
          paymentOrderNoFromProps: this.props.paymentOrderNo,
          paymentOrderNoFromPayResultInfo: _.get(payResultInfo, 'paymentOrderNo'),
          paymentOrderNoFromQuery: queryParams.paymentOrderNo,
        })
        return
      }

      this.props.downloadPDF({
        orderToken: orderToken,
        // Always send as string to avoid bigint precision loss
        paymentOrderNo: `${paymentOrderNo}`,
        // eslint-disable-next-line handle-callback-err
      }, (error, result) => {
        if (error) {
          console.error('PDF下载失败:', error)
        } else if (result?.fileLink) {
          window.location.href = result.fileLink
        }
      })
    }
    if (loading) {
      return (
        <Flex
          direction='column'
          justify='center'
          style={{ height: '100%' }}
          className={!forceMobile && classes.componentsResult}
        >
          <div className='text-center'>
            <Loading processing />
          </div>
          <WhiteSpace size='sm' />
          {status === 'P' && !payResultInfo ? (
            <React.Fragment>
              <div className='font-16'>
                <b>{intl().formatMessage(messages.Nopaymentfoundredirecting)}</b>
              </div>
              <p className='font-12'>{intl().formatMessage(messages.Tryanotherpaymentmethodtocontinue)}</p>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className='font-16'>
                <b>{intl().formatMessage(messages.Processing)}</b>
              </div>
              <p className='font-12'>{intl().formatMessage(messages.Afewsecondstowaitforyourpaymentresult)}</p>
            </React.Fragment>
          )}
        </Flex>
      )
    }

    return (
      <div className={rootClassName} id='container'>
        <div className={classes.resultContent}>
          <WhiteSpace />
          <div>
            <img width={72} src={ENUM?.[status]?.img} />
          </div>
          <WhiteSpace size='sm' />
          <b className='paragraph_regular'>{ENUM[status]?.text}</b>
          <WhiteSpace size='xs' />
          {payResultInfo.payAmount && (
            <h2 className='text_main heading_2'>
              {payResultInfo.payAmount.currency}&nbsp;
              {payResultInfo.payAmount.amount}
            </h2>
          )}
          {payResultInfo.discountAmount && (
            <div className='text-line-through text_placeholder'>
              <b>{handleAmount(payResultInfo.orderAmount)}</b>
            </div>
          )}
          {shouldAutoRedirect && (
            <React.Fragment>
              <WhiteSpace size='xs' />
              <AutoRedirectCountdown
                merchantReturnUrl={this.props.merchantReturnUrl}
                pageName={this.props.pageName}
                payResultInfo={payResultInfo}
              />
            </React.Fragment>
          )}
          {failMessage && (
            <React.Fragment>
              <WhiteSpace size='xs' />
              <div className={classes.failMessage}>{failMessage}</div>
            </React.Fragment>
          )}
          {showDownloadReceipt() && typeof this.props.downloadPDF === 'function' && (
            <div className={classes.downloadReceipt}>
              <span onClick={handleDownloadPDF}>Download Receipt</span>
            </div>
          )}
          <WingBlank>
            <ToSaveCard
              showLoginFields={showLoginFields}
              showLoginDrawer={this.showLoginDrawer}
              payResultInfo={payResultInfo}
              LoginDrawerVisible={this.state.LoginDrawerVisible}
              onChange={(LoginDrawerVisible) => {
                this.setState({
                  LoginDrawerVisible,
                })
              }}
            />
          </WingBlank>
          <WhiteSpace />
          <hr style={{ background: 'var(--gray-4)' }} />
          <div className={classes.orderInfo}>
            {payResultInfo.currencyIcon && (
              <React.Fragment>
                <img src={payResultInfo.currencyIcon} width='48' />
                <WhiteSpace />
              </React.Fragment>
            )}
            <WhiteSpace />
            <Row style={{ marginBottom: '20px' }}>
              <Col span={12} className='text-left'>
                {intl().formatMessage(messages.OrderAmount)}
              </Col>
              <Col span={12} className='text-right'>
                {handleAmount(payResultInfo.orderAmount)}
              </Col>
            </Row>
            {payResultInfo.discountAmount && (
              <React.Fragment>
                <Row className='yellow' style={{ marginBottom: '20px' }}>
                  <Col span={12} className='text-left'>
                    {intl().formatMessage(messages.Discount)}
                  </Col>
                  <Col span={12} className='text-right'>
                    {handleAmount({
                      ...payResultInfo.discountAmount,
                      direction: '0',
                    })}
                  </Col>
                </Row>
                <WhiteSpace size='xs' />
              </React.Fragment>
            )}
            {status === 'S' && (
              <Row style={{ marginBottom: '20px' }}>
                <Col span={12} className='text-left'>
                  {intl().formatMessage(messages.PaymentMethod)}
                </Col>
                <Col span={12} className='text-right'>
                  <Flex align='center' justify='end'>
                    {resolvedPaymentMethod && isGooglePayMethod(resolvedPaymentMethod) && (
                      <>
                        {/* 官方 Google Pay 标志按原始比例显示 24px 高；留白见 styles 的 .googlePayMark（含从右往左布局） */}
                        <img
                          src={GooglePayMark}
                          height={24}
                          alt='Google Pay'
                          className={classes.googlePayMark}
                        />
                        {getPaymentMethodDescription(resolvedPaymentMethod)}
                      </>
                    )}
                    {resolvedPaymentMethod && !isGooglePayMethod(resolvedPaymentMethod) && (
                      <>
                        <PaymentMethodIcon
                          height={12}
                          tabType={resolvedPaymentMethod.type}
                          {...resolvedPaymentMethod.extension}
                        />{' '}
                        {getPaymentMethodDescription(resolvedPaymentMethod)}
                      </>
                    )}
                  </Flex>
                </Col>
              </Row>
            )}
            {payResultInfo.tradeSrcVoucherNo && (
              <Flex style={{ marginBottom: '20px' }}>
                <span className='text-left'>{intl().formatMessage(messages.MerchantOrderNo)}</span>
                <Flex.Item className='text-right'>
                  <CopyToClipboard>{payResultInfo.tradeSrcVoucherNo}</CopyToClipboard>
                </Flex.Item>
              </Flex>
            )}
            {payResultInfo.orderNo && (
              <Flex style={{ marginBottom: '20px' }}>
                <span className='text-left'>{intl().formatMessage(messages.OrderNo)}</span>
                <Flex.Item className='text-right'>
                  <CopyToClipboard>{payResultInfo.orderNo}</CopyToClipboard>
                </Flex.Item>
              </Flex>
            )}
            {payResultHasDccDetails(payResultInfo) && (
              <React.Fragment>
                <hr style={{ background: 'var(--gray-4)', margin: '20px 0' }} />
                <Row style={{ marginBottom: '12px' }}>
                  <Col span={24} className='text-left'>
                    <b>{intl().formatMessage(messages.DccConversion)}</b>
                  </Col>
                </Row>
                {payResultInfo.dccTotalAmount &&
                  payResultInfo.dccTotalAmount.amount != null &&
                  payResultInfo.dccTotalAmount.amount !== '' && (
                  <Row style={{ marginBottom: '12px' }}>
                    <Col span={12} className='text-left'>
                      {intl().formatMessage(messages.DccChargedAmount)}
                    </Col>
                    <Col span={12} className='text-right'>
                      {handleAmount(
                        {
                          amount: payResultInfo.dccTotalAmount.amount,
                          currency: payResultInfo.dccTotalAmount.currency,
                        },
                        payResultInfo.dccTotalAmount.currency,
                        getDccAmountDecimalPlaces(payResultInfo.dccTotalAmount.currency)
                      )}
                    </Col>
                  </Row>
                )}
                {payResultInfo.dccExchangeRate != null && payResultInfo.dccExchangeRate !== '' && (
                  <Row style={{ marginBottom: '12px' }}>
                    <Col span={12} className='text-left'>
                      {intl().formatMessage(messages.DccExchangeRate)}
                    </Col>
                    <Col span={12} className='text-right'>
                      {formatExchangeRateForDisplay(payResultInfo.dccExchangeRate)}
                    </Col>
                  </Row>
                )}
                {normalizeMarkupPercentDisplay(payResultInfo.dccMarkupRate) != null && (
                  <Row style={{ marginBottom: '12px' }}>
                    <Col span={12} className='text-left'>
                      {intl().formatMessage(messages.DccMarkup)}
                    </Col>
                    <Col span={12} className='text-right'>
                      {formatPercent(Number(normalizeMarkupPercentDisplay(payResultInfo.dccMarkupRate)))}
                    </Col>
                  </Row>
                )}
                {payResultInfo.dccQuoteId && (
                  <Flex style={{ marginBottom: '12px' }}>
                    <span className='text-left'>{intl().formatMessage(messages.DccQuoteId)}</span>
                    <Flex.Item className='text-right'>
                      <CopyToClipboard>{payResultInfo.dccQuoteId}</CopyToClipboard>
                    </Flex.Item>
                  </Flex>
                )}
                <hr style={{ background: 'var(--gray-4)', margin: '20px 0' }} />
                <div className={classes.statement}>
                  I HAVE BEEN OFFERED A CHOICE OF CURRENCIES AND ACCEPTED [{String(dccAcceptedCurrency || '').toUpperCase()}]. THIS CURRENCY CONVERSION IS PROVIDED BY THE MERCHANT.
                </div>
              </React.Fragment>
            )}
            {payResultInfo.installmentInfo && (
              <React.Fragment>
                <hr style={{ background: 'var(--gray-4)', margin: '20px 0' }} />
                <div className={classes.installmentSection}>
                  <div className={classes.installmentHeader}>
                    <img
                      src={require('../PaymentMethods/VisaInstallments/images/visa_watermark.png')}
                      alt='VISA'
                      className={classes.visaLogo}
                    />
                  </div>
                  {payResultInfo.installmentInfo.externalReferenceNo && (
                    <Row style={{ marginBottom: '12px' }}>
                      <Col span={12} className='text-left'>
                        {intl().formatMessage(messages.PlanAcceptanceId)}
                      </Col>
                      <Col span={12} className='text-right'>
                        <CopyToClipboard>{payResultInfo.installmentInfo.externalReferenceNo}</CopyToClipboard>
                      </Col>
                    </Row>
                  )}
                  {payResultInfo.installmentInfo.installmentNumber && (
                    <Row style={{ marginBottom: '12px' }}>
                      <Col span={12} className='text-left'>
                        {intl().formatMessage(messages.InstallmentNumber)}
                      </Col>
                      <Col span={12} className='text-right'>
                        {payResultInfo.installmentInfo.installmentNumber}
                      </Col>
                    </Row>
                  )}
                  {payResultInfo.installmentInfo.installmentFirstAmount && (
                    <Row style={{ marginBottom: '12px' }}>
                      <Col span={12} className='text-left'>
                        {intl().formatMessage(messages.MonthlyPayment)}
                      </Col>
                      <Col span={12} className='text-right'>
                        {(() => {
                          const frequency = formatInstallmentFrequency(
                            payResultInfo.installmentInfo.installmentFrequencySingle ||
                              payResultInfo.installmentInfo.installmentFrequency
                          )
                          return `${handleAmount(payResultInfo.installmentInfo.installmentFirstAmount)}${
                            frequency ? `/${frequency}` : ''
                          }`
                        })()}
                      </Col>
                    </Row>
                  )}
                  <Row style={{ marginBottom: '12px' }}>
                    <Col span={12} className='text-left'>
                      {intl().formatMessage(messages.MonthlyRate)}
                    </Col>
                    <Col span={12} className='text-right'>
                      {formatPercent(
                        yearlyRateToMonthlyPercent(
                          payResultInfo.installmentInfo.yearlyRate ??
                            payResultInfo.installmentInfo.annualPercentageRate
                        )
                      )}
                    </Col>
                  </Row>
                  {payResultInfo.installmentInfo.totalInstallmentAmount && (
                    <Row style={{ marginBottom: '12px' }}>
                      <Col span={12} className='text-left'>
                        {intl().formatMessage(messages.TotalInstallmentAmount)}
                      </Col>
                      <Col span={12} className='text-right'>
                        {handleAmount(payResultInfo.installmentInfo.totalInstallmentAmount)}
                      </Col>
                    </Row>
                  )}
                  {payResultInfo.installmentInfo.installmentUpfrontFee && (
                    <Row style={{ marginBottom: '12px' }}>
                      <Col span={12} className='text-left'>
                        {intl().formatMessage(messages.ProcessingFees)}
                      </Col>
                      <Col span={12} className='text-right'>
                        {handleAmount(payResultInfo.installmentInfo.installmentUpfrontFee)}
                      </Col>
                    </Row>
                  )}
                  {payResultInfo.installmentInfo.installmentTAndCContent && (
                    <React.Fragment>
                      <hr style={{ background: 'var(--gray-4)', margin: '16px 0' }} />
                      <div className={classes.termsSection}>
                        <div className={classes.termsTitle}>
                          {intl().formatMessage(messages.TermsAndConditions)}
                        </div>
                        <div className={classes.termsContent}>
                          <div style={{ whiteSpace: 'pre-line' }}>
                            {payResultInfo.installmentInfo.installmentTAndCContent}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </React.Fragment>
            )}
          </div>
          {banner && siteConfig.isMobile && (
            <>
              <hr style={{ background: 'var(--gray-4)' }} />
              <WingBlank>
                <WhiteSpace />
                <img
                  src={banner.iconUrl}
                  alt='banner'
                  className='w-full'
                  onClick={() => {
                    // eslint-disable-next-line no-return-assign
                    window.location.href = banner.targetUrl
                  }}
                />
              </WingBlank>
            </>
          )}
          {siteConfig.isMobile && (
            <TipsResult visible={this.state.tipsSaveResultVisible} toggle={this.handleTipsSaveResultVisible} siteConfig={siteConfig} />
          )}
          {siteConfig.isMobile && hideCopyright !== true && !shouldHideCopyrightForPaymentMethod && (
            <Flex align='center' justify='center' style={{ marginTop: '24px', marginBottom: '32px' }}>
              <PoweredBy />
            </Flex>
          )}
        </div>

        <HandleButtons
          {...this.props}
          shouldAutoRedirect={shouldAutoRedirect}
          handleTipsSaveResultVisible={this.handleTipsSaveResultVisible}
        />
      </div>
    )
  }
}
